import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setSignedOutHandler } from '@/lib/api/client';
import { unwrap } from '@/lib/api/errors';
import { tokens } from '@/lib/api/tokens';
import type { RegisterRequest, User } from '@/lib/api/types';
import { outbox } from '@/lib/offline';
import { clearSnapshots, withSnapshot, writeSnapshot } from '@/lib/offline/snapshot';

type Status = 'loading' | 'anonymous' | 'authenticated';

interface Session {
  status: Status;
  user: User | undefined;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (input: RegisterRequest) => Promise<void>;
  signInWithGoogle: (credential: string, extra?: { locale?: 'en' | 'hr'; currency?: string }) => Promise<void>;
  signOut: () => void;
  refreshUser: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);
const ME_KEY = ['me'] as const;

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  // Tokens live outside React; bump this to re-evaluate them after sign-in or sign-out.
  const [epoch, setEpoch] = useState(0);
  const hasSession = Boolean(tokens.refresh());

  const me = useQuery({
    queryKey: ME_KEY,
    ...withSnapshot('me', async () => unwrap(await api.GET('/auth/me'))),
    enabled: hasSession,
    retry: false,
    staleTime: 5 * 60_000,
  });

  // Cached profile data always goes. Queued writes go only when the user chooses to sign out: an expired session must
  // not throw away changes made offline, they are delivered after the next sign-in.
  const endSession = useCallback(
    (discardQueuedWrites: boolean) => {
      tokens.clear();
      tokens.setWorkspaceId(null);
      clearSnapshots();
      if (discardQueuedWrites) void outbox.clear();
      qc.clear();
      setEpoch((e) => e + 1);
    },
    [qc],
  );
  const signOut = useCallback(() => endSession(true), [endSession]);

  useEffect(() => {
    setSignedOutHandler(() => endSession(false));
  }, [endSession]);

  const adopt = useCallback(
    (access: string, refresh: string, user?: User) => {
      tokens.setPair(access, refresh);
      if (user) {
        qc.setQueryData(ME_KEY, user);
        writeSnapshot('me', user);
      }
      else void qc.invalidateQueries({ queryKey: ME_KEY });
      setEpoch((e) => e + 1);
    },
    [qc],
  );

  const value = useMemo<Session>(() => {
    const authed = hasSession && Boolean(me.data) && tokens.refresh() !== null;
    let status: Status = 'anonymous';
    if (authed) status = 'authenticated';
    else if (hasSession && me.isPending) status = 'loading';
    return {
      status,
      user: authed ? me.data : undefined,
      async signIn(email, password) {
        const t = unwrap(await api.POST('/auth/login', { body: { email, password } }));
        tokens.setPair(t.access_token, t.refresh_token);
        const user = unwrap(await api.GET('/auth/me'));
        qc.setQueryData(ME_KEY, user);
        writeSnapshot('me', user);
        setEpoch((e) => e + 1);
      },
      async signUp(input) {
        const r = unwrap(await api.POST('/auth/register', { body: input }));
        adopt(r.tokens.access_token, r.tokens.refresh_token, r.user);
      },
      async signInWithGoogle(credential, extra) {
        const r = unwrap(await api.POST('/auth/google', { body: { credential, ...extra } }));
        adopt(r.tokens.access_token, r.tokens.refresh_token, r.user);
      },
      signOut,
      async refreshUser() {
        await qc.invalidateQueries({ queryKey: ME_KEY });
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- epoch is the signal that tokens changed
  }, [hasSession, me.data, me.isPending, qc, adopt, signOut, epoch]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}
